import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from './endpoints';
import { getAuthState } from '../store/useAuthStore';
import { getRequestState } from '../store/useRequestStore';
import type { RefreshTokenResponse, ApiError, ApiResponse } from './types';

/**
 * Base API URL from environment variable
 * Defaults to localhost:3000 for development
 */
const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/**
 * Axios instance with base configuration
 */
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Track if we're currently refreshing the token
 * Prevents multiple simultaneous refresh requests
 */
let isRefreshing = false;

/**
 * Queue of requests waiting for token refresh
 */
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

/**
 * Process the queue after token refresh
 */
const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Request interceptor
 * Automatically injects Bearer token into Authorization header
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = getAuthState();

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Extract request ID from response headers
 * This enables full-stack traceability for debugging and support
 */
const extractRequestId = (response: AxiosResponse | undefined): string | null => {
  if (!response?.headers) return null;
  return response.headers['x-request-id'] || null;
};

/**
 * Response interceptor
 * - Extracts x-request-id header for traceability
 * - Handles 401 errors with automatic token refresh and request retry
 */
apiClient.interceptors.response.use(
  (response) => {
    // Extract and store request ID from successful responses
    const requestId = extractRequestId(response);
    if (requestId) {
      getRequestState().setRequestId(requestId);
    }
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    // Extract request ID from error responses for traceability
    const requestId = extractRequestId(error.response);
    const status = error.response?.status || 0;
    const url = error.config?.url || 'unknown';
    
    if (requestId) {
      getRequestState().setRequestError(requestId, status, url);
    }
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Don't retry if:
    // 1. No original request config
    // 2. Already retried
    // 3. Error is from login/refresh endpoints
    // 4. Not a 401 error
    if (
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url === API_ENDPOINTS.AUTH.LOGIN ||
      originalRequest.url === API_ENDPOINTS.AUTH.REFRESH ||
      error.response?.status !== 401
    ) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          },
          reject: (err: unknown) => {
            reject(err);
          },
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { refreshToken, setTokens, clearAuth } = getAuthState();

    // No refresh token - clear auth and reject
    if (!refreshToken) {
      clearAuth();
      isRefreshing = false;
      // Only redirect to login if we're on a protected route (admin routes)
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    try {
      // Attempt to refresh the token
      // Backend returns wrapped response: { success, data: { accessToken, refreshToken } }
      const response = await axios.post<ApiResponse<RefreshTokenResponse>>(
        `${BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
        response.data.data;

      // Update tokens in store
      setTokens(newAccessToken, newRefreshToken);

      // Process queued requests with new token
      processQueue(null, newAccessToken);

      // Retry original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }

      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear auth and redirect to login
      processQueue(refreshError, null);
      clearAuth();

      // Only redirect to login if we're on a protected route (admin routes)
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        window.location.href = '/login';
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Type-safe GET request
 */
export async function get<T>(url: string, config?: Parameters<typeof apiClient.get>[1]) {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

/**
 * Type-safe POST request
 */
export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: Parameters<typeof apiClient.post>[2]
) {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe PATCH request
 */
export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: Parameters<typeof apiClient.patch>[2]
) {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe DELETE request
 */
export async function del<T>(url: string, config?: Parameters<typeof apiClient.delete>[1]) {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

/**
 * Upload file with multipart/form-data
 */
export async function uploadFile<T>(
  url: string,
  file: File,
  fieldName = 'image',
  additionalData?: Record<string, string>
) {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await apiClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export default apiClient;

